import React, { useCallback } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { Text } from '@atlaskit/primitives/compiled';

import type { UpdateWorkItemAllFieldTypesAction } from '../../../../../common/types';
import { FieldTooltipContent } from '../../../../../common/ui/FieldTooltipContent';
import {
	type RegisterTooltipProvider,
	useRegisterTooltipContentProvider,
} from '../../../../../common/utils/useRegisterTooltipContentProvider';
import { getActionData } from '../../../utils';

import type { sprintField_updateWorkItemAllFieldTypesActionRenderer$key } from './__generated__/sprintField_updateWorkItemAllFieldTypesActionRenderer.graphql';

const messages = defineMessages({
	set: {
		id: 'update-work-item-all-fields-action-renderer.ui.action-card.sprint-field.set',
		defaultMessage: 'Move work item to sprint',
		description: 'Label for the set action in the sprint field',
	},
	remove: {
		id: 'update-work-item-all-fields-action-renderer.ui.action-card.sprint-field.remove',
		defaultMessage: 'Remove work item from sprint',
		description: 'Label for the remove action in the sprint field',
	},
});

export type Props = {
	queryRef: sprintField_updateWorkItemAllFieldTypesActionRenderer$key;
	action: UpdateWorkItemAllFieldTypesAction;
	registerTooltipProvider?: RegisterTooltipProvider;
};

export const SprintField = ({
	queryRef,
	action,
	registerTooltipProvider,
}: Props): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	const { fieldKey, fieldValue, fieldReasoning } = getActionData(action);

	const suggestedSprintId =
		typeof fieldValue === 'string' ? fieldValue.trim() : fieldValue?.toString() || null;

	const data = useFragment(
		graphql`
			fragment sprintField_updateWorkItemAllFieldTypesActionRenderer on JiraSprintField
			@argumentDefinitions(sprintAris: { type: "[ID!]!" }) {
				fieldId
				name
				selectedSprintsConnection {
					edges {
						node {
							name
						}
					}
				}
				sprints(filterById: { optionIds: $sprintAris, operation: ALLOW }) {
					edges {
						node {
							sprintId
							name
						}
					}
				}
			}
		`,
		queryRef,
	);

	const isFieldRenderable = Boolean(data?.fieldId && data.fieldId === fieldKey);
	const currentSprint = data?.selectedSprintsConnection?.edges?.[0]?.node ?? null;
	const currentSprintName = currentSprint?.name ?? null;

	const sprints = data?.sprints?.edges?.map((edge) => edge?.node) ?? [];
	const suggestedSprint = sprints.find((sprint) => sprint?.sprintId === suggestedSprintId);

	const toolTipContentProvider = useCallback(
		() => (
			<FieldTooltipContent
				reasoning={fieldReasoning}
				fieldName={data?.name}
				current={currentSprintName}
				suggested={suggestedSprint?.name}
			/>
		),
		[fieldReasoning, data?.name, currentSprintName, suggestedSprint?.name],
	);

	useRegisterTooltipContentProvider({
		registerTooltipProvider,
		fieldId: fieldKey,
		provider: toolTipContentProvider,
		enabled: isFieldRenderable,
	});

	if (!isFieldRenderable) {
		return null;
	}

	return (
		<Text
			color="color.text.subtle"
			maxLines={1}
			testId="update-work-item-all-fields-action-renderer.ui.action-card.card-fields-list.sprint-field"
		>
			{suggestedSprintId ? formatMessage(messages.set) : formatMessage(messages.remove)}
		</Text>
	);
};
